import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { ContextMenu, FlexMiddle, Loader, Popover, Table } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import { ReactComponent as BalancesEmptyIcon } from '@ubeya/shared-web/assets/calendar-add.svg';
import { formatBalanceUnitSuffix, isMoneyBalance } from '../leaveBalanceUtils';
import { formatDate, formatMoney, formatNumber } from '../leaveFormatters';
import { Badge, MiniEmpty, MiniEmptyCta, MiniEmptyHint, MiniEmptyTitle, NumericCell } from './styled';

const PlanName = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  border-bottom: 1px dotted ${({ theme }) => theme.colors.grey3};
  text-align: left;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    border-bottom-color: ${({ theme }) => theme.colors.primary};
  }
`;

const AvailableCell = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;
`;

const AvailableAmount = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-variant-numeric: tabular-nums;
  font-weight: 600;

  ${({ $tone, theme }) => {
    if ($tone === 'negative')
      return css`
        color: ${theme.colors.redNegative};
        background: ${theme.colors.redLight};
      `;
    if ($tone === 'positive')
      return css`
        color: ${theme.colors.greenPositive};
        background: ${theme.colors.greenExtraLight};
      `;
    return css`
      color: ${theme.colors.grey4};
      background: transparent;
    `;
  }}
`;

const PendingSubline = styled.span`
  font-size: 11px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.orangeChanges};
  margin-top: 2px;
`;

const MoneyAmount = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 600;
`;

const MoneyDaysHint = styled.span`
  color: ${({ theme }) => theme.colors.grey4};
  font-size: 12px;
  margin-left: 6px;
`;

const HoverCard = styled.div`
  min-width: 280px;
  max-width: 340px;
  padding: 14px 16px;
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme }) => theme.colors.black};
`;

const HoverCardHeader = styled(FlexMiddle)`
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const HoverCardTitle = styled.span`
  font-weight: 600;
`;

const HoverCardGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, auto) minmax(0, 1fr);
  gap: 6px 12px;
`;

const HoverCardLabel = styled.span`
  color: ${({ theme }) => theme.colors.grey4};
  font-size: 12px;
`;

const HoverCardValue = styled.span`
  color: ${({ theme }) => theme.colors.black};
`;

const HoverCardStrip = styled.div`
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.colors.grey2};
  color: ${({ theme }) => theme.colors.grey4};
  font-size: 12px;
`;

const formatPayRule = (config, t) => {
  switch (config?.pay?.rule) {
    case 'base-100':
      return t('employeeLeave_hoverCard_payRule_base100');
    case 'base-pct':
      return t('employeeLeave_hoverCard_payRule_basePct', { pct: config?.pay?.percent ?? 0 });
    case 'fixed-hourly':
      return t('employeeLeave_hoverCard_payRule_fixedHourly');
    case 'lookback-avg':
      return t('employeeLeave_hoverCard_payRule_lookbackAvg');
    case 'none':
      return t('employeeLeave_hoverCard_payRule_none');
    default:
      return '-';
  }
};

const formatCap = (config, balance, t) => {
  const annual = config?.caps?.annual;
  if (!annual?.enabled || !annual?.amount) return '-';
  const unit = balance?.unit === 'money' ? '' : ` ${annual.unit || balance?.unit || ''}`;
  return `${annual.amount}${unit} / ${t('year')}`;
};

const formatYearEnd = (config, t) => {
  const carryover = config?.yearEnd?.carryover;
  const payout = config?.yearEnd?.payoutUnused;
  if (carryover?.enabled) return t('employeeLeave_hoverCard_yearEnd_carryover');
  if (payout) return t('employeeLeave_hoverCard_yearEnd_payout');
  return t('employeeLeave_hoverCard_yearEnd_forfeit');
};

const LeaveHoverCardContent = ({ balance }) => {
  const { t } = useContext(ConfigContext);
  const formattingMap = useFormattingMap();
  if (!balance) return null;

  const isPaid = balance.isPaid !== false;
  const isManualCycle = balance.yearCycleKind === 'manual';
  const config = balance.config;
  const yearWindow = balance.yearWindow ?? null;

  return (
    <HoverCard>
      <HoverCardHeader>
        <HoverCardTitle>{balance.leaveTypeName}</HoverCardTitle>
        <Badge $tone={isPaid ? 'paid' : 'unpaid'}>
          {isPaid ? t('employeeLeave_hoverCard_paid') : t('employeeLeave_hoverCard_unpaid')}
        </Badge>
        {isManualCycle && <Badge $tone="manual">{t('employeeLeave_hoverCard_manualCycle')}</Badge>}
      </HoverCardHeader>
      <HoverCardGrid>
        <HoverCardLabel>{t('employeeLeave_hoverCard_howItsPaid')}</HoverCardLabel>
        <HoverCardValue>{formatPayRule(config, t)}</HoverCardValue>
        <HoverCardLabel>{t('employeeLeave_hoverCard_accrual')}</HoverCardLabel>
        <HoverCardValue>{balance.accrualMode || '-'}</HoverCardValue>
        <HoverCardLabel>{t('employeeLeave_hoverCard_cap')}</HoverCardLabel>
        <HoverCardValue>{formatCap(config, balance, t)}</HoverCardValue>
      </HoverCardGrid>
      <HoverCardStrip>
        {t('employeeLeave_hoverCard_yearEnd')}: {formatYearEnd(config, t)}
      </HoverCardStrip>
      {yearWindow !== null && (
        <HoverCardStrip>
          {t('employeeLeave_balance_yearWindow', {
            start: formatDate(formattingMap, yearWindow.start),
            end: formatDate(formattingMap, yearWindow.end)
          })}
        </HoverCardStrip>
      )}
    </HoverCard>
  );
};

const BalanceTable = ({ management, isLoading, onAdjust, onAddUsed }) => {
  const { t } = useContext(ConfigContext);
  const formattingMap = useFormattingMap();

  const balances = management?.balances || [];

  const columns = useMemo(
    () => [
      {
        Header: t('employeeLeave_balanceCol_plan'),
        dataKey: 'plan',
        Cell: ({ rowData }) => {
          const [isOpen, setIsOpen] = useState(false);
          const openHover = useCallback(() => setIsOpen(true), []);
          const closeHover = useCallback(() => setIsOpen(false), []);

          return (
            <Popover
              isPopoverOpen={isOpen}
              onClickOutside={closeHover}
              positions={['right', 'bottom', 'left', 'top']}
              showArrow={false}
              // EmployeeCard renders inside a FormModal (z-index ≥ 5). The
              // Popover wrapper defaults containerStyle.zIndex to 4 — popovers
              // shown from inside a modal paint BEHIND the modal and are
              // invisible. Bump above the modal so the hover card surfaces.
              containerStyle={{ zIndex: 6, top: '6px' }}
              popoverContent={<LeaveHoverCardContent balance={rowData} />}
              triggerContent={
                <PlanName
                  type="button"
                  onMouseEnter={openHover}
                  onMouseLeave={closeHover}
                  onFocus={openHover}
                  onBlur={closeHover}>
                  {rowData.leaveTypeName}
                </PlanName>
              }
            />
          );
        }
      },
      {
        Header: t('employeeLeave_balanceCol_carryover'),
        dataKey: 'carryover',
        Cell: ({ rowData }) => <NumericCell>{formatNumber(rowData.carryover ?? 0)}</NumericCell>
      },
      {
        Header: t('employeeLeave_balanceCol_accrued'),
        dataKey: 'accrued',
        Cell: ({ rowData }) => <NumericCell>{formatNumber(rowData.accrued ?? 0)}</NumericCell>
      },
      {
        Header: t('employeeLeave_balanceCol_taken'),
        dataKey: 'taken',
        Cell: ({ rowData }) => <NumericCell $tone="negative">{formatNumber(rowData.taken ?? 0)}</NumericCell>
      },
      {
        Header: t('employeeLeave_balanceCol_available'),
        dataKey: 'available',
        Cell: ({ rowData }) => {
          const rawAvailable = rowData.available ?? rowData.balance ?? 0;
          const pending = Number(rowData.pending) || 0;
          const availableAfterPending = Number(rawAvailable) - pending;
          const hasPending = pending > 0;
          const tone = availableAfterPending < 0 ? 'negative' : availableAfterPending > 0 ? 'positive' : undefined;
          if (isMoneyBalance(rowData)) {
            const avgDayPay = Number(rowData.avgDayPay) || 0;
            const approxDays = avgDayPay > 0 ? Math.floor(availableAfterPending / avgDayPay) : null;
            return (
              <AvailableCell>
                <AvailableAmount $tone={tone}>
                  <MoneyAmount>{formatMoney(formattingMap, availableAfterPending)}</MoneyAmount>
                  {approxDays !== null && (
                    <MoneyDaysHint>{t('employeeLeave_moneyPot_approxDays', { count: approxDays })}</MoneyDaysHint>
                  )}
                </AvailableAmount>
                {hasPending && (
                  <PendingSubline>
                    {t('employeeLeave_balance_pendingApproval', { count: formatMoney(formattingMap, pending) })}
                  </PendingSubline>
                )}
              </AvailableCell>
            );
          }
          return (
            <AvailableCell>
              <AvailableAmount $tone={tone}>
                {formatNumber(availableAfterPending)}
                {formatBalanceUnitSuffix(rowData, t)}
              </AvailableAmount>
              {hasPending && (
                <PendingSubline>
                  {t('employeeLeave_balance_pendingApproval', {
                    count: `${formatNumber(pending)}${formatBalanceUnitSuffix(rowData, t)}`
                  })}
                </PendingSubline>
              )}
            </AvailableCell>
          );
        }
      },
      {
        Header: '',
        dataKey: 'actions',
        width: 48,
        Cell: ({ rowData }) => (
          <ContextMenu
            hideIconBackground
            usePortal
            openFromRight
            options={[
              { label: t('employeeLeave_kebab_adjustAllowance'), onClick: () => onAdjust(rowData) },
              { label: t('employeeLeave_kebab_addUsedDays'), onClick: () => onAddUsed(rowData) }
            ]}
          />
        )
      }
    ],
    [t, formattingMap, onAdjust, onAddUsed]
  );

  if (isLoading) return <Loader />;

  if (!management || !management.accountContractV2Id) {
    return null;
  }

  if (balances.length === 0) {
    return (
      <MiniEmpty>
        <BalancesEmptyIcon />
        <MiniEmptyTitle>{t('employeeLeave_emptyBalances_title')}</MiniEmptyTitle>
        <MiniEmptyHint>{t('employeeLeave_emptyBalances_hint')}</MiniEmptyHint>
        <MiniEmptyCta
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onAdjust(null);
          }}>
          {t('employeeLeave_emptyBalances_cta')}
        </MiniEmptyCta>
      </MiniEmpty>
    );
  }

  return (
    <Table isVirtualized={false} headerRowHeight={50} autoHeight data={balances} columns={columns} shadow={false} />
  );
};

export default BalanceTable;
